<template>
  <div class="vendor-list">
    <div v-if="loading" class="vendor-list__state">
      <Wait :message="'Loading vendors...'" />
    </div>

    <div v-else-if="errorMessage" class="vendor-list__state vendor-list__state--error">
      {{ errorMessage }}
    </div>

    <div v-else-if="vendors.length > 0" class="vendor-list__grid">
      <VendorPlatforms
        v-for="vendor in vendors"
        :key="vendor.id || vendor.code || vendor.name"
        :vendor="vendor"
        :expanded="expandedVendorKey === getVendorKey(vendor)"
        @toggle="handleToggleVendor"
      />
    </div>

    <div v-else class="vendor-list__state">
      No vendors found.
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import VendorPlatforms from '@/components/Platforms/VendorPlatforms.vue';
import Wait from '@/components/utils/Wait.vue';

const emit = defineEmits(['vendor-toggle']);

const vendors = ref([]);
const loading = ref(true);
const errorMessage = ref('');
const expandedVendorKey = ref(null);
const dataConnect = new DatastoreConnect();

onMounted(async () => {
  await loadVendors();
});

async function loadVendors() {
  loading.value = true;
  errorMessage.value = '';

  try {
    const response = await dataConnect.api('platform.list.vendors', {});

    if (response?.error) {
      throw new Error(response.error.message || 'Failed to load vendors');
    }

    vendors.value = Array.isArray(response?.result)
      ? response.result
      : (Array.isArray(response?.result?.items) ? response.result.items : []);
  } catch (error) {
    errorMessage.value = String(error?.message || error || 'Failed to load vendors');
    vendors.value = [];
  } finally {
    loading.value = false;
  }
}

function getVendorKey(vendor) {
  return String(vendor?.id || vendor?.code || vendor?.name || '');
}

function handleToggleVendor(vendor) {
  const key = getVendorKey(vendor);
  if (!key) {
    return;
  }

  const isClosing = expandedVendorKey.value === key;
  expandedVendorKey.value = isClosing ? null : key;
  emit('vendor-toggle', {
    vendor,
    expanded: !isClosing,
  });
}

</script>

<style lang="scss" scoped>
.vendor-list {
  display: flex;
  flex-direction: column;
  width: 600px;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

.vendor-list__grid {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  max-width: 100%;
}

.vendor-list__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  width: 100%;
  border-radius: 12px;
  @include themify() {
    color: themed('body-text-color-secondary');
    background: themed('color-white-100');
    border: 1px dashed themed('color-primary-200');
  }
}

.vendor-list__state--error {
  @include themify() {
    color: themed('color-status-error');
  }
}

</style>
